import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Avatar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import SortIcon from "@mui/icons-material/Sort";
import ReportService from "../Services/Service";
import { motion, AnimatePresence } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function VendorShowPage() {
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateErrors, setDateErrors] = useState({
    fromDate: "",
    toDate: "",
  });
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState("card"); // "card" or "grid"
  const [anchorEl, setAnchorEl] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState(""); // "name" | "company" | "designation" | ""
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"

  useEffect(() => {
    ReportService.setApiUrl(
      sessionStorage.getItem("APIUrl") + "/api/RoleMapping",
    );
  }, []);

  const navigate = useNavigate();

  // const fetchVendors = async () => {
  //   try {
  //     const res = await ReportService.getAll();
  //     setVendors(res.data || []);
  //   } catch (err) {
  //     console.error("Error fetching vendors", err);
  //   }
  // };

  const fetchVendors = async () => {
    try {
      const empNo = sessionStorage.getItem("EmpNo");

      const res = await ReportService.getReport(empNo, fromDate, toDate);

      setVendors(res.data || []);
    } catch (err) {
      console.error("Error fetching vendors", err);
    }
  };
  const handleOpen = (vendor) => {
    setSelectedVendor(vendor);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedVendor(null);
  };

  // Handlers for menu
  const handleSortClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleSortClose = () => {
    setAnchorEl(null);
  };
  const handleSortSelect = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    handleSortClose();
  };
  const handleSearch = () => {
    const errors = {
      fromDate: "",
      toDate: "",
    };

    if (!fromDate) {
      errors.fromDate = "Please select Start Date";
    }

    if (!toDate) {
      errors.toDate = "Please select End Date";
    }

    if (fromDate && toDate && fromDate > toDate) {
      errors.toDate = "End Date cannot be earlier than Start Date";
    }

    setDateErrors(errors);

    if (errors.fromDate || errors.toDate) {
      return;
    }

    fetchVendors();
  };

  // Filter vendors by searchTerm
  const filtered = vendors.filter((vendor) =>
    [
      vendor.company_Name,
      vendor.name,
      vendor.designation,
      vendor.contact,
      vendor.email,
      vendor.webSite,
      vendor.addresh,
      vendor.eventName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );
  // Sort a copy of filtered results (avoid mutating original array)
  const filteredVendors = [...filtered].sort((a, b) => {
    if (!sortField) return 0; // no sorting

    const getFieldValue = (vendor, field) => {
      if (field === "company") return (vendor.company_Name || "").toLowerCase();
      return (vendor[field] || "").toLowerCase(); // "name" or "designation"
    };

    const aValue = getFieldValue(a, sortField);
    const bValue = getFieldValue(b, sortField);

    // Use localeCompare for more robust string sorting
    const cmp = aValue.localeCompare(bValue, undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return sortOrder === "asc" ? cmp : -cmp;
  });

  return (
    <div style={{ width: "100%", paddingLeft: 50, paddingRight: 50 }}>
      <Box
        sx={{
          maxWidth: "1260px",
          margin: "0 auto",
        }}
      >
        {/* Search + Toggle + Sort */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/DashboardFinal")}
            >
              Back to Home
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <TextField
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setDateErrors((prev) => ({
                  ...prev,
                  fromDate: "",
                }));
              }}
              error={!!dateErrors.fromDate}
              helperText={
                <span style={{ fontSize: "11px" }}>{dateErrors.fromDate}</span>
              }
              sx={{ width: 170 }}
            />

            <TextField
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setDateErrors((prev) => ({
                  ...prev,
                  toDate: "",
                }));
              }}
              error={!!dateErrors.toDate}
              helperText={
                <span style={{ fontSize: "11px" }}>{dateErrors.toDate}</span>
              }
              sx={{ width: 170 }}
            />

            <Button
              variant="contained"
              size="small"
              sx={{
                height: "40px",
                px: 3,
              }}
              onClick={handleSearch}
            >
              Search
            </Button>
          </Box>
          <Box
            sx={{
              borderBottom: "1px solid #e0e0e0",
              mb: 3,
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search Vendor"
              variant="outlined"
              size="small"
              sx={{ width: 280 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {/* Sort + Toggle */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton onClick={handleSortClick}>
                <SortIcon />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleSortClose}
              >
                {/* Name */}
                <MenuItem onClick={() => handleSortSelect("name", "asc")}>
                  Name A-Z
                </MenuItem>
                <MenuItem onClick={() => handleSortSelect("name", "desc")}>
                  Name Z-A
                </MenuItem>

                {/* Company */}
                <MenuItem onClick={() => handleSortSelect("company", "asc")}>
                  Company A-Z
                </MenuItem>
                <MenuItem onClick={() => handleSortSelect("company", "desc")}>
                  Company Z-A
                </MenuItem>

                {/* Designation */}
                <MenuItem
                  onClick={() => handleSortSelect("designation", "asc")}
                >
                  Designation A-Z
                </MenuItem>
                <MenuItem
                  onClick={() => handleSortSelect("designation", "desc")}
                >
                  Designation Z-A
                </MenuItem>
              </Menu>

              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(e, newView) => newView && setView(newView)}
                size="small"
                sx={{
                  borderRadius: "30px",
                  overflow: "hidden",
                  boxShadow: 2,
                }}
              >
                <ToggleButton
                  value="card"
                  sx={{
                    borderRadius: "30px",
                    px: 3,
                    py: 1,
                    "&.Mui-selected": {
                      backgroundColor: "#1976d2",
                      color: "#fff",
                    },
                    "&:not(.Mui-selected)": {
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                    },
                    "&:hover": { backgroundColor: "#bbdefb" },
                  }}
                >
                  <ViewModuleIcon />
                </ToggleButton>

                <ToggleButton
                  value="grid"
                  sx={{
                    borderRadius: "30px",
                    px: 3,
                    py: 1,
                    "&.Mui-selected": {
                      backgroundColor: "#2e7d32",
                      color: "#fff",
                    },
                    "&:not(.Mui-selected)": {
                      backgroundColor: "#e8f5e9",
                      color: "#2e7d32",
                    },
                    "&:hover": { backgroundColor: "#c8e6c9" },
                  }}
                >
                  <ViewListIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>

        {/* Card View */}
        <AnimatePresence mode="wait">
          {view === "card" && (
            <motion.div
              key="cardView"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.4 }}
            >
              <Grid container spacing={3} justifyContent="center">
                {filteredVendors.map((vendor, index) => (
                  <Grid item xs={12} sm={6} md={4} key={vendor.sl_No ?? index}>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Card
                        sx={{
                          width: 320,
                          height: 200,
                          borderRadius: "16px",
                          boxShadow: 3,
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                          transition:
                            "transform 0.25s ease, box-shadow 0.25s ease",
                          "&:hover": {
                            boxShadow: 6,
                            transform: "translateY(-6px) scale(1.02)",
                            zIndex: 100,
                          },
                        }}
                        onClick={() => handleOpen(vendor)}
                        onMouseEnter={() => setHovered(index)}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <CardContent
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "primary.main",
                              width: 56,
                              height: 56,
                              mb: 1,
                            }}
                          >
                            {vendor.company_Name?.charAt(0) ??
                              vendor.name?.charAt(0) ??
                              ""}
                          </Avatar>

                          <Typography
                            variant="h6"
                            sx={{ mb: 1, textAlign: "center" }}
                          >
                            {vendor.company_Name}
                          </Typography>

                          {hovered === index && (
                            <>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                👤 {vendor.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                💼 {vendor.designation}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                📞 {vendor.contact}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                📧 {vendor.email}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                🎪 {vendor.eventName}
                              </Typography>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          )}

          {/* Grid/Table View */}
          {view === "grid" && (
            <motion.div
              key="gridView"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.4 }}
            >
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Designation</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Event Name</TableCell>
                      <TableCell>Created By</TableCell>
                      <TableCell>Created Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredVendors.map((vendor, idx) => (
                      <TableRow
                        key={vendor.sl_No ?? idx}
                        hover
                        onClick={() => handleOpen(vendor)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>{vendor.company_Name}</TableCell>
                        <TableCell>{vendor.name}</TableCell>
                        <TableCell>{vendor.designation}</TableCell>
                        <TableCell>{vendor.email}</TableCell>
                        <TableCell>{vendor.contact}</TableCell>
                        <TableCell>
                          {vendor.eventName && vendor.eventName.trim() !== ""
                            ? vendor.eventName
                            : "Not Applicable"}
                        </TableCell>
                        <TableCell>{vendor.createdByName}</TableCell>

                        <TableCell>
                          {vendor.createdDate
                            ? new Date(vendor.createdDate).toLocaleDateString(
                                "en-GB",
                              )
                            : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Details Modal */}
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "16px", boxShadow: 6, padding: 2 },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            Company Details
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ textAlign: "center" }}>
            {selectedVendor && (
              <>
                <Avatar
                  sx={{ bgcolor: "primary.main", width: 64, height: 64, mb: 2 }}
                >
                  {selectedVendor.company_Name?.charAt(0) ??
                    selectedVendor.name?.charAt(0) ??
                    ""}
                </Avatar>

                <Typography variant="h6">
                  {selectedVendor.company_Name}
                </Typography>
                <Typography variant="body1">
                  👤 {selectedVendor.name}
                </Typography>
                <Typography variant="body1">
                  💼 {selectedVendor.designation}
                </Typography>
                <Typography variant="body1">
                  📞 {selectedVendor.contact}
                </Typography>
                <Typography variant="body1">
                  📧 {selectedVendor.email}
                </Typography>
                <Typography variant="body1">
                  🎪 {selectedVendor.eventName}
                </Typography>
                {selectedVendor.webSite && (
                  <Typography variant="body1">
                    🌐 {selectedVendor.webSite}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  📍 {selectedVendor.addresh}
                </Typography>

                <Typography variant="body1">
                  🖊️ Created By: {selectedVendor.createdByName}
                </Typography>

                <Typography variant="body1">
                  📅 Created Date:{" "}
                  {selectedVendor.createdDate
                    ? new Date(selectedVendor.createdDate).toLocaleDateString(
                        "en-GB",
                      )
                    : ""}
                </Typography>
              </>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </div>
  );
}
